import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }
  
  return data.data as T;
}

// ============================================
// TYPES
// ============================================

export type EventType = 'meetup' | 'workshop' | 'meditation' | 'special';

export interface Event {
  id: string;
  title: string;
  description: string | null;
  eventType: EventType;
  startTime: string;
  endTime: string;
  location: string | null;
  latitude: number | null;
  longitude: number | null;
  xpReward: number;
  maxAttendees: number | null;
  requiresLocation: boolean;
  locationRadius: number | null;
  imageUrl: string | null;
  isCheckedIn: boolean;
  checkinCount: number;
  canCheckin: boolean;
}

export interface CheckinResult {
  xpGained: number;
  levelUp: boolean;
  newLevel: number;
  questsCompleted: string[];
}

export interface Attendee {
  oderId: string;
  userName: string;
  characterName: string | null;
  archetype: string | null;
  checkedInAt: string;
}

export interface UserCheckin {
  event: Event;
  checkedInAt: string;
}

// ============================================
// API CALLS
// ============================================

export async function getUpcomingEvents(): Promise<{ events: Event[] }> {
  return request('/events');
}

export async function getPastEvents(limit?: number): Promise<{ events: Event[] }> {
  const params = limit ? `?limit=${limit}` : '';
  return request(`/events/past${params}`);
}

export async function getCalendarEvents(year: number, month: number): Promise<{ events: Event[] }> {
  return request(`/events/calendar?year=${year}&month=${month}`);
}

export async function getEventDetails(eventId: string): Promise<{ event: Event }> {
  return request(`/events/${eventId}`);
}

export async function getEventAttendees(eventId: string): Promise<{ attendees: Attendee[] }> {
  return request(`/events/${eventId}/attendees`);
}

export async function checkinToEvent(
  eventId: string,
  location?: { latitude: number; longitude: number }
): Promise<CheckinResult> {
  return request(`/events/${eventId}/checkin`, {
    method: 'POST',
    body: JSON.stringify(location || {}),
  });
}

export async function checkinByQrCode(
  qrCode: string,
  location?: { latitude: number; longitude: number }
): Promise<CheckinResult> {
  return request('/events/checkin-qr', {
    method: 'POST',
    body: JSON.stringify({ qrCode, ...location }),
  });
}

export async function getUserCheckins(limit?: number): Promise<{ checkins: UserCheckin[] }> {
  const params = limit ? `?limit=${limit}` : '';
  return request(`/events/my-checkins${params}`);
}

// ============================================
// V2 API (Events with passport system)
// ============================================

export interface RecommendedEvent {
  id: string;
  title: string;
  description: string | null;
  format: string | null;
  socialFormat: string | null;
  targetChars: string[];
  startsAt: string;
  location: string | null;
  xpReward: number;
  maxParticipants: number | null;
  currentParticipants: number;
  score: number;
}

export async function getRecommendedEvents(): Promise<{ events: RecommendedEvent[] }> {
  return request('/events/recommended');
}

export async function registerForEvent(eventId: string): Promise<{ message: string }> {
  return request(`/events/${eventId}/register`, { method: 'POST' });
}

export async function attendEvent(eventId: string, checkinCode: string): Promise<{ xpAwarded: number }> {
  return request(`/events/${eventId}/attend`, {
    method: 'POST',
    body: JSON.stringify({ checkinCode }),
  });
}

export async function submitEvidence(eventId: string, artifactUrl: string): Promise<{ xpAwarded: number }> {
  return request(`/events/${eventId}/evidence`, {
    method: 'POST',
    body: JSON.stringify({ artifactUrl }),
  });
}
